import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Search, User as UserIcon, Pencil,
  Calendar as CalendarIcon, Clock, MapPin, Stethoscope,
  ChevronDown, ChevronRight, UserPlus, AlertTriangle,
  History,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import {
  getPediaChairWarning,
  hasChairCapability,
} from "@/lib/chairCapabilities";
import {
  CLINIC_TIME_ZONE,
  clinicAddMinutes,
  clinicInstantFromIsoMinute,
  clinicIsoMinuteFromInstant,
  clinicTodayDateString,
} from "@/lib/clinicTime";
import {
  alignMinutesToSlotInterval,
  buildDurationOptions,
  defaultDurationMinutes,
  normalizeSlotIntervalMinutes,
} from "@/lib/calendarGranularity";
import { formatDoctorName } from "@/lib/doctorName";
import { useOngoingTreatmentRollout } from "@/context/OngoingTreatmentRolloutContext";
import { buildTimeChipSlots } from "./TimeChipGrid";
import TimeSelect from "./TimeSelect";
import MvCaseImportDialog from "@/components/treatment/MvCaseImportDialog";
import {
  activeMvItems,
  calendarBookingRows,
  calendarPlanWork,
  isGenericMvProcedure,
  mvItemScopeLabel,
} from "@/lib/mvImport";

// =====================================================================
// NewAppointmentPanel V2 — see memory/project_book_appointment_v2.md
// for design rationale (Cameron 2026-04-30).
//
// Two opening modes drive the same form:
//   A) From a calendar slot click: date/time/chair pre-filled. Show
//      them as a compact context strip with a [✎] toggle that swaps
//      to inline pickers when the receptionist needs to nudge any of
//      them. Doctor + duration always visible (those aren't carried
//      by the slot click).
//   B) From the "+ Book" topbar button: no slot context, so the
//      pickers render expanded by default.
//
// Walk-in: a checkbox in More Options. When ticked, the Book button
// becomes "Book & Check In →" and the create flow hits quick_check_in
// (atomic create + status=Checked In). When unticked, normal
// create_appointment (status=Scheduled).
//
// V3 will add: Treatment plan items linking section (per Cameron's
// pen-and-paper TN refinement). Slot reserved below patient picker.
// =====================================================================

const ACTIVE_ORTHO_STATUSES = new Set(["active_pending_bonding", "active", "delinquent", "retention"]);
const BONDING_READY_STATUS = "active_pending_bonding";
const CONTACT_METHODS = [
  { value: "no_preference", label: "No preference" },
  { value: "phone", label: "Phone" },
  { value: "sms", label: "SMS" },
  { value: "email", label: "Email" },
];
const BOOKING_TUTORIAL_PATIENT = {
  id: "tutorial-booking-patient",
  patient_number: "DEMO-0001",
  full_name: "Juan Dela Cruz",
  phone: "09171234567",
  email: "juan@example.test",
  preferred_doctor_id: null,
};
const BOOKING_TUTORIAL_PROCEDURE = {
  id: "tutorial-booking-procedure",
  name: "Oral Prophylaxis",
  code: "PROPHY",
  archived: false,
  suggested_duration_minutes: 45,
  default_fee_centavos: 120000,
};

export default function NewAppointmentPanel({
  doctors = [], chairs = [], settings = null,
  initialStart = null, initialEnd = null, initialChairId = null,
  tutorialDemo = false,
  onCreated, onCancel,
}) {
  const { enabled: ongoingTreatmentEnabled } = useOngoingTreatmentRollout();
  const canImportOngoingCase = ongoingTreatmentEnabled;
  const fromSlot = !!initialStart;
  const slotIntervalMinutes = normalizeSlotIntervalMinutes(settings?.slot_interval_minutes);
  const fallbackDurationMin = defaultDurationMinutes(settings, slotIntervalMinutes);

  // Patient
  const [patientSearch, setPatientSearch] = useState("");
  const [patients, setPatients] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [patientDropOpen, setPatientDropOpen] = useState(false);
  const patientDropRef = useRef(null);
  const [newPatientOpen, setNewPatientOpen] = useState(false);
  const [newPatient, setNewPatient] = useState(() => initialNewPatient());
  const [creatingPatient, setCreatingPatient] = useState(false);

  // Procedure
  const [procedures, setProcedures] = useState([]);
  const [procSearch, setProcSearch] = useState("");
  const [procDropOpen, setProcDropOpen] = useState(false);
  const [pickedProc, setPickedProc] = useState(null);
  const [mvImportOpen, setMvImportOpen] = useState(false);
  const [mvImportedItem, setMvImportedItem] = useState(null);
  const [activeMvBundle, setActiveMvBundle] = useState(null);
  const [loadingMvBundle, setLoadingMvBundle] = useState(false);
  const [bookablePlanWork, setBookablePlanWork] = useState(null);
  const [loadingBookablePlanWork, setLoadingBookablePlanWork] = useState(false);
  const [pickedMvItemId, setPickedMvItemId] = useState("");
  const [pickedMvPlanId, setPickedMvPlanId] = useState("");
  const procDropRef = useRef(null);

  // Ortho scheduling context. This is what lets Doctor View render the inline
  // ortho chairside panel only for actual ortho appointments.
  const [orthoRows, setOrthoRows] = useState([]);
  const [loadingOrthoRows, setLoadingOrthoRows] = useState(false);
  const [pickedOrthoCaseId, setPickedOrthoCaseId] = useState("");
  const [orthoVisitType, setOrthoVisitType] = useState("");

  // Schedule
  const [start, setStart] = useState(initialStart ? toLocalIsoMinute(initialStart) : "");
  const [durationMin, setDurationMin] = useState(
    initialStart && initialEnd ? minutesBetween(initialStart, initialEnd, fallbackDurationMin) : fallbackDurationMin
  );
  const [chairId, setChairId] = useState(initialChairId || "");
  const [doctorId, setDoctorId] = useState("");

  // Context strip vs inline pickers
  const [editingContext, setEditingContext] = useState(!fromSlot);

  // More options
  const [moreOpen, setMoreOpen] = useState(false);
  const [walkIn, setWalkIn] = useState(false);
  const [notes, setNotes] = useState("");

  // Submit
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // ---------------------------------------------------------------------
  // Initial loads
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (tutorialDemo) {
      setProcedures([BOOKING_TUTORIAL_PROCEDURE]);
      return;
    }
    (async () => {
      const { data, error: e } = await supabase.rpc("list_clinic_procedures");
      if (!e) setProcedures(data ?? []);
    })();
  }, [tutorialDemo]);

  // Close dropdowns when the user clicks outside the search input + list.
  // mousedown (not click) so the close fires before any focus change and
  // before the click event bubbles — but contains() still wins for clicks
  // on result rows, so picking a row still works.
  useEffect(() => {
    function handleClickOutside(e) {
      if (patientDropRef.current && !patientDropRef.current.contains(e.target)) {
        setPatientDropOpen(false);
      }
      if (procDropRef.current && !procDropRef.current.contains(e.target)) {
        setProcDropOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync inputs if parent updates the slot (user clicks a different one
  // while the panel is already open).
  useEffect(() => {
    if (!initialStart) return;
    setStart(toLocalIsoMinute(initialStart));
    if (initialEnd) setDurationMin(minutesBetween(initialStart, initialEnd, fallbackDurationMin));
    if (initialChairId !== null && initialChairId !== undefined) setChairId(initialChairId || "");
    setEditingContext(false);
    setError(null);
  }, [initialStart, initialEnd, initialChairId, fallbackDurationMin]);

  // Patient search debounce — list_patients takes p_search only (no p_limit);
  // we trim to 10 client-side. Earlier code passed p_limit and silently broke.
  useEffect(() => {
    if (!patientSearch || patientSearch.length < 2) {
      setPatients([]); setSearching(false);
      return;
    }
    if (tutorialDemo) {
      setPatients([BOOKING_TUTORIAL_PATIENT]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      const { data, error: e } = await supabase.rpc("list_patients", { p_search: patientSearch });
      setSearching(false);
      if (!e) setPatients((data ?? []).slice(0, 10));
    }, 300);
    return () => clearTimeout(handle);
  }, [patientSearch, tutorialDemo]);

  // When patient changes, smart-default the doctor. Priority order:
  //   1. owning_dentist_id (commercial-credit owner; FK doctors.id) —
  //      this is the canonical "this patient's dentist" for booking
  //      purposes per the patient-ownership spec.
  //   2. preferred_doctor_id (clinical preference; FK profiles.id) —
  //      legacy fallback for patients with no owning dentist yet.
  // We fetch owning_dentist_id directly because list_patients (the
  // search RPC powering `picked`) doesn't return it; one extra SELECT
  // per patient pick is cheap and keeps this commit decoupled from a
  // search-RPC schema change.
  const [owningDentistId, setOwningDentistId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!picked?.id) {
      setOwningDentistId(null);
      setOrthoRows([]);
      setActiveMvBundle(null);
      setLoadingMvBundle(false);
      setBookablePlanWork(null);
      setLoadingBookablePlanWork(false);
      setPickedMvItemId("");
      setPickedMvPlanId("");
      setPickedOrthoCaseId("");
      setOrthoVisitType("");
      setLoadingOrthoRows(false);
      return;
    }
    if (tutorialDemo) {
      setOwningDentistId(null);
      setOrthoRows([]);
      setActiveMvBundle(null);
      setBookablePlanWork([]);
      setLoadingOrthoRows(false);
      setLoadingMvBundle(false);
      setLoadingBookablePlanWork(false);
      setPickedMvItemId("");
      setPickedMvPlanId("");
      setPickedOrthoCaseId("");
      setOrthoVisitType("");
      return;
    }
    (async () => {
      setLoadingOrthoRows(true);
      setLoadingMvBundle(true);
      setLoadingBookablePlanWork(true);
      const [patientRes, orthoRes, mvRes, bookableRes] = await Promise.all([
        supabase
        .from("patients")
        .select("owning_dentist_id")
        .eq("id", picked.id)
          .maybeSingle(),
        supabase.rpc("list_patient_ortho_cases", { p_patient_id: picked.id }),
        ongoingTreatmentEnabled
          ? supabase.rpc("get_patient_bundle", { p_patient_id: picked.id })
          : Promise.resolve({ data: null, error: null }),
        supabase.rpc("list_patient_unbooked_plan_work", { p_patient_id: picked.id }),
      ]);
      if (cancelled) return;
      setOwningDentistId(patientRes.data?.owning_dentist_id ?? null);
      setLoadingOrthoRows(false);
      setLoadingMvBundle(false);
      setLoadingBookablePlanWork(false);
      setActiveMvBundle(mvRes.error ? null : (mvRes.data || null));
      setBookablePlanWork(bookableRes.error
        ? null
        : (Array.isArray(bookableRes.data) ? bookableRes.data : []));
      if (orthoRes.error) {
        setOrthoRows([]);
        setPickedOrthoCaseId("");
        setOrthoVisitType("");
        return;
      }
      const active = (Array.isArray(orthoRes.data) ? orthoRes.data : [])
        .filter((row) => ACTIVE_ORTHO_STATUSES.has(row?.case?.status));
      setOrthoRows(active);
      setPickedOrthoCaseId("");
      setOrthoVisitType("");
    })();
    return () => { cancelled = true; };
  }, [ongoingTreatmentEnabled, picked?.id, tutorialDemo]);

  useEffect(() => {
    if (ongoingTreatmentEnabled) return;
    setActiveMvBundle(null);
    setMvImportOpen(false);
    setMvImportedItem(null);
    setPickedMvItemId("");
    setPickedMvPlanId("");
  }, [ongoingTreatmentEnabled]);

  useEffect(() => {
    setMvImportedItem(null);
    setMvImportOpen(false);
    setPickedMvItemId("");
    setPickedMvPlanId("");
  }, [picked?.id]);

  useEffect(() => {
    setMvImportedItem(null);
    setMvImportOpen(false);
    if (pickedProc?.id) setPickedMvPlanId("");
  }, [pickedProc?.id]);

  useEffect(() => {
    if (!pickedOrthoCaseId) {
      setOrthoVisitType("");
      return;
    }
    setPickedProc(null);
    setProcSearch("");
    setProcDropOpen(false);
    const row = orthoRows.find((candidate) => candidate?.case?.id === pickedOrthoCaseId);
    if (!row) return;
    setOrthoVisitType((prev) => prev || defaultOrthoVisitType(row));
  }, [pickedOrthoCaseId, orthoRows]);

  useEffect(() => {
    if (doctorId) return;
    if (owningDentistId) {
      setDoctorId(owningDentistId);
      return;
    }
    if (picked?.preferred_doctor_id) {
      const match = doctors.find((d) => d.profile_id === picked.preferred_doctor_id);
      if (match) setDoctorId(match.id);
    }
  }, [picked, doctors, owningDentistId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When procedure changes, prefill duration from suggested if present.
  useEffect(() => {
    if (pickedProc?.suggested_duration_minutes) {
      setDurationMin(alignMinutesToSlotInterval(pickedProc.suggested_duration_minutes, slotIntervalMinutes));
    }
  }, [pickedProc, slotIntervalMinutes]);

  // When Walk-in is toggled ON, pre-fill chair + doctor from clinic
  // settings so the receptionist doesn't have to pick what's already
  // their default. Only fills empty fields — doesn't clobber a value
  // the user already set. Reverts nothing on toggle-off.
  useEffect(() => {
    if (!walkIn || !settings) return;
    if (!chairId  && settings.walk_in_default_chair_id)  setChairId(settings.walk_in_default_chair_id);
    if (!doctorId && settings.walk_in_default_doctor_id) setDoctorId(settings.walk_in_default_doctor_id);
  }, [walkIn, settings]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------
  const filteredProcs = useMemo(() => {
    const q = procSearch.trim().toLowerCase();
    const visible = procedures.filter((p) => !p.archived);
    if (!q) return visible.slice(0, 50);
    return visible.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.code || "").toLowerCase().includes(q) ||
      (p.short_code || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [procedures, procSearch]);

  const endIso = useMemo(() => {
    if (!start || !durationMin) return null;
    const d = clinicInstantFromIsoMinute(start);
    if (Number.isNaN(d.getTime())) return null;
    return clinicAddMinutes(d, durationMin).toISOString();
  }, [start, durationMin]);

  const selectedChair = useMemo(() => chairs.find((c) => c.id === chairId) || null, [chairs, chairId]);
  const chairName  = selectedChair?.name;
  const doctorName = useMemo(() => doctors.find((d) => d.id === doctorId)?.display_name, [doctors, doctorId]);
  const selectedOrthoRow = useMemo(
    () => orthoRows.find((row) => row?.case?.id === pickedOrthoCaseId) || null,
    [orthoRows, pickedOrthoCaseId]
  );
  const activeMvOverviewItems = useMemo(
    () => activeMvItems(activeMvBundle),
    [activeMvBundle],
  );
  const fallbackMvWork = useMemo(
    () => activeMvOverviewItems.map((item) => ({
      ...item,
      plan_id: activeMvBundle?.plan_id,
      plan_code: activeMvBundle?.mv_code,
      plan_kind: "mv",
      balance_centavos: activeMvBundle?.balance_centavos,
    })),
    [activeMvBundle, activeMvOverviewItems],
  );
  const visiblePlanWork = useMemo(
    () => calendarPlanWork(activeMvBundle, bookablePlanWork ?? fallbackMvWork)
      .filter((item) => (
        ongoingTreatmentEnabled
        || item?.plan_kind !== "mv"
      )),
    [activeMvBundle, bookablePlanWork, fallbackMvWork, ongoingTreatmentEnabled],
  );
  const visibleBookingRows = useMemo(
    () => calendarBookingRows(visiblePlanWork),
    [visiblePlanWork],
  );
  const bookableBookingCount = useMemo(
    () => visibleBookingRows.filter((row) => row.booking_available).length,
    [visibleBookingRows],
  );
  const selectedMvPlanGroup = useMemo(
    () => visibleBookingRows.find((row) => (
      row.row_kind === "mv_group" && row.plan_id === pickedMvPlanId
    )) || null,
    [pickedMvPlanId, visibleBookingRows],
  );
  const matchingPlanItems = useMemo(
    () => pickedProc?.id ? visiblePlanWork.filter(
      (item) => item?.plan_kind !== "mv" && item?.procedure_id === pickedProc.id,
    ) : [],
    [pickedProc?.id, visiblePlanWork],
  );
  const matchingMvPlanGroup = useMemo(
    () => pickedProc?.id ? visibleBookingRows.find((row) => (
      row.row_kind === "mv_group"
      && row.items.some((item) => item?.procedure_id === pickedProc.id)
    )) || null : null,
    [pickedProc?.id, visibleBookingRows],
  );
  const selectedPlanItem = useMemo(() => {
    if (mvImportedItem?.item_id) return {
      ...mvImportedItem,
      plan_code: activeMvBundle?.mv_code,
      plan_kind: "mv",
      procedure_name: pickedProc?.name,
    };
    return matchingPlanItems.find((item) => item.item_id === pickedMvItemId) || null;
  }, [activeMvBundle?.mv_code, matchingPlanItems, mvImportedItem, pickedMvItemId, pickedProc?.name]);
  const allowedOrthoVisitTypes = useMemo(
    () => new Set(orthoVisitChoices(selectedOrthoRow).map((choice) => choice.value)),
    [selectedOrthoRow],
  );
  const pediaChairWarning = useMemo(() => getPediaChairWarning({
    patient: picked,
    procedure: pickedProc,
    chair: selectedChair,
    asOf: start ? clinicInstantFromIsoMinute(start) : new Date(),
  }), [picked, pickedProc, selectedChair, start]);

  const canBook = picked && start && durationMin > 0
    && (!walkIn || (doctorId && chairId))
    && selectedPlanItem?.booking_available !== false
    && selectedMvPlanGroup?.booking_available !== false
    && !(matchingMvPlanGroup && matchingPlanItems.length === 0 && !selectedMvPlanGroup);
  const durationOptions = useMemo(
    () => buildDurationOptions(slotIntervalMinutes, [
      durationMin,
      fallbackDurationMin,
      pickedProc?.suggested_duration_minutes,
    ]),
    [durationMin, fallbackDurationMin, pickedProc?.suggested_duration_minutes, slotIntervalMinutes],
  );

  function selectRegularVisit() {
    setPickedOrthoCaseId("");
    setOrthoVisitType("");
  }

  function selectOrthoVisit() {
    setPickedMvPlanId("");
    const defaultCaseId = pickedOrthoCaseId || orthoRows[0]?.case?.id || "";
    if (defaultCaseId) setPickedOrthoCaseId(defaultCaseId);
  }

  function selectPlanItemForBooking(item) {
    const procedure = procedures.find((candidate) => candidate.id === item?.procedure_id);
    if (!procedure) {
      toast.error("This ongoing treatment is not available in the clinic procedure list.");
      return;
    }
    setPickedOrthoCaseId("");
    setOrthoVisitType("");
    setPickedProc(procedure);
    setPickedMvItemId(item.item_id);
    setPickedMvPlanId("");
    setProcSearch("");
    setProcDropOpen(false);
  }

  function selectMvPlanForBooking(group) {
    if (!group?.plan_id || group.booking_available === false) return;
    setPickedOrthoCaseId("");
    setOrthoVisitType("");
    setPickedProc(null);
    setPickedMvItemId("");
    setPickedMvPlanId(group.plan_id);
    setProcSearch("");
    setProcDropOpen(false);
  }

  useEffect(() => {
    if (mvImportedItem?.item_id) {
      setPickedMvItemId(mvImportedItem.item_id);
      return;
    }
    setPickedMvItemId((current) => {
      if (matchingPlanItems.some((item) => item.item_id === current)) return current;
      return matchingPlanItems.length === 1 ? matchingPlanItems[0].item_id : "";
    });
  }, [matchingPlanItems, mvImportedItem]);

  // ---------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------
  async function handleCreatePatient(e) {
    e.preventDefault();
    if (creatingPatient) return;
    const fullName = newPatient.full_name.trim();
    const phone = newPatient.phone.trim();
    const email = newPatient.email.trim();
    if (!fullName) { setError("Patient full name is required."); return; }
    if (!phone) { setError("Patient phone is required."); return; }

    setCreatingPatient(true);
    setError(null);
    const payload = {
      full_name: fullName,
      phone,
      email: email || null,
      preferred_doctor_id: null,
      preferred_contact_method: newPatient.preferred_contact_method,
      recall_opt_in: true,
    };
    const { data, error: e2 } = await supabase.rpc("create_patient", { p_data: payload });
    setCreatingPatient(false);
    if (e2) {
      setError(e2.message ?? "Could not create patient.");
      return;
    }

    const created = {
      id: data?.patient_id,
      patient_number: data?.patient_number,
      full_name: data?.full_name || fullName,
      phone,
      email: email || null,
      status: data?.status || "New",
      preferred_doctor_id: null,
    };
    setPicked(created);
    setPatientSearch(created.full_name);
    setPatients([]);
    setPatientDropOpen(false);
    setNewPatientOpen(false);
    setNewPatient(initialNewPatient());
    toast.success("Patient created.");
  }

  async function handleBook(overrideBuffer) {
    // Coerce to strict boolean — guard against accidental event-object
    // passthrough from a bare onClick={handleBook} that would
    // serialize the React fiber and crash the JSON.stringify in
    // supabase-js.
    const _override = overrideBuffer === true;
    if (!picked) { setError("Pick a patient first."); return; }
    if (!start)  { setError("Start time is required."); return; }
    if (!durationMin) { setError("Duration is required."); return; }
    if (tutorialDemo) {
      setError(null);
      toast.success("Practice appointment complete. Nothing was added to the clinic calendar.");
      window.dispatchEvent(new CustomEvent("tutorial:booking-demo-created"));
      onCreated?.();
      return;
    }
    if (walkIn && (!doctorId || !chairId)) {
      setError("Walk-in check-in needs a doctor and chair.");
      return;
    }
    if (pickedOrthoCaseId && !orthoVisitType) {
      setError("Pick the ortho visit type for this appointment.");
      return;
    }
    if (pickedOrthoCaseId && !selectedOrthoRow) {
      setError("Pick a valid ortho case for this patient.");
      return;
    }
    if (pickedOrthoCaseId && !allowedOrthoVisitTypes.has(orthoVisitType)) {
      setError(orthoVisitType === "bonding"
        ? "Bonding can only be booked for an ortho case that is active pending bonding."
        : "That ortho visit type is not valid for this case state.");
      return;
    }
    if (selectedPlanItem?.booking_available === false) {
      setError("This MV item is already active and no additional booking is currently available. Open the existing appointment instead of creating another case.");
      return;
    }
    if (selectedMvPlanGroup?.booking_available === false) {
      setError("This MV already has an active appointment. Open that appointment instead of booking another one.");
      return;
    }
    if (matchingMvPlanGroup && matchingPlanItems.length === 0 && !selectedMvPlanGroup) {
      setError(`Use Book ${matchingMvPlanGroup.plan_code || "MV"} next visit so every treatment in the MV is included.`);
      return;
    }
    if (matchingPlanItems.length > 0 && !selectedPlanItem) {
      setError("Choose which treatment-plan item this appointment continues.");
      return;
    }

    // Client-side advance-booking check (server enforces too).
    if (!walkIn && settings?.advance_booking_days) {
      const limitMs = settings.advance_booking_days * 24 * 60 * 60 * 1000;
      const startMs = clinicInstantFromIsoMinute(start).getTime();
      if (startMs - Date.now() > limitMs) {
        setError(`This clinic allows booking at most ${settings.advance_booking_days} days in advance. Adjust the date or update Settings → Hours & Booking.`);
        return;
      }
    }

    setBusy(true); setError(null);
    const startInstant = clinicInstantFromIsoMinute(start);
    if (Number.isNaN(startInstant.getTime())) {
      setBusy(false);
      setError("Start time is invalid.");
      return;
    }
    const startIso = startInstant.toISOString();

    if (walkIn) {
      const { error: e } = await supabase.rpc("quick_check_in", {
        p_input: {
          patient_id:       picked.id,
          doctor_id:        doctorId,
          chair_id:         chairId,
          scheduled_start:  startIso,
          duration_minutes: durationMin,
          notes:            notes || null,
          procedure_id:     selectedMvPlanGroup ? null : (pickedProc?.id ?? null),
          ortho_case_id:    pickedOrthoCaseId || null,
          ortho_visit_type: pickedOrthoCaseId ? orthoVisitType : null,
          treatment_plan_item_id: selectedMvPlanGroup ? null : (selectedPlanItem?.item_id || null),
          mv_treatment_plan_item_id: selectedMvPlanGroup
            ? null
            : (selectedPlanItem?.plan_kind === "mv" ? selectedPlanItem.item_id : null),
          mv_treatment_plan_id: selectedMvPlanGroup?.plan_id || null,
        },
      });
      setBusy(false);
      if (e) { setError(e.message ?? "Could not check in."); return; }
      toast.success("Walk-in checked in.");
    } else {
      const { error: e } = await supabase.rpc("create_appointment", {
        p_input: {
          patient_id:      picked.id,
          doctor_id:       doctorId || null,
          chair_id:        chairId  || null,
          scheduled_start: startIso,
          scheduled_end:   endIso,
          walk_in:         false,
          override_buffer: _override,
          notes:           notes || null,
          procedure_id:    selectedMvPlanGroup ? null : (pickedProc?.id ?? null),
          ortho_case_id:   pickedOrthoCaseId || null,
          ortho_visit_type: pickedOrthoCaseId ? orthoVisitType : null,
          treatment_plan_item_id: selectedMvPlanGroup ? null : (selectedPlanItem?.item_id || null),
          mv_treatment_plan_item_id: selectedMvPlanGroup
            ? null
            : (selectedPlanItem?.plan_kind === "mv" ? selectedPlanItem.item_id : null),
          mv_treatment_plan_id: selectedMvPlanGroup?.plan_id || null,
        },
      });
      setBusy(false);
      if (e) {
        // Server raised a buffer-overlap. If we haven't already tried
        // overriding, surface a confirmation toast that retries with
        // override_buffer=true. The error message contains the offending
        // window; show it so the receptionist knows what they're
        // overlapping with.
        if (!_override && /BUFFER_OVERLAP/i.test(e.message || "")) {
          const detail = (e.message || "").replace(/^.*?BUFFER_OVERLAP:\s*/, "");
          toast.warning("Buffer overlap", {
            description: detail,
            duration: 12000,
            action: {
              label: "Book anyway",
              onClick: () => handleBook(true),
            },
          });
          return;
        }
        setError(e.message ?? "Could not book.");
        return;
      }
      toast.success("Appointment booked.");
    }
    onCreated?.();
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  return (
    <div className="flex flex-col h-full" data-tutorial-id="booking-panel">
      <div className="flex-1 overflow-y-auto p-5 space-y-4">

        {/* ── Context strip / inline pickers ───────────────────────────── */}
        {!editingContext && fromSlot ? (
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 flex items-start justify-between gap-3">
            <div className="text-[13px] text-slate-700 space-y-0.5 min-w-0">
              <div className="flex items-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span className="font-medium">{formatDateLine(start)}</span>
                <span className="text-slate-400">·</span>
                <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span className="font-medium">{formatTime(start)}</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">{durationMin} min</span>
              </div>
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span>{chairName || "No chair"}</span>
                <span className="text-slate-400">·</span>
                <Stethoscope className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span>{doctorName || "No doctor"}</span>
              </div>
            </div>
            <button
              onClick={() => setEditingContext(true)}
              className="text-slate-500 hover:text-slate-900 p-1 rounded hover:bg-white flex-shrink-0"
              aria-label="Edit date, time, chair, or doctor"
              data-testid="new-appt-edit-context"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Date</Label>
                <input
                  type="date" value={splitLocalIsoMinute(start).date}
                  onChange={(e) => setStart(joinLocalIsoMinute(e.target.value, splitLocalIsoMinute(start).time || "09:00"))}
                  data-testid="new-appt-date"
                  data-tutorial-id="booking-date"
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
              <div>
                <Label>Chair</Label>
                <select value={chairId} onChange={(e) => setChairId(e.target.value)}
                  data-testid="new-appt-chair"
                  data-tutorial-id="booking-chair"
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded">
                  <option value="">No chair</option>
                  {chairs.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{hasChairCapability(c, "pedia") ? " · Pedia" : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div data-tutorial-id="booking-time-grid">
              <Label>Time</Label>
              <TimeSelect
                value={splitLocalIsoMinute(start).time}
                onChange={(t) => setStart(joinLocalIsoMinute(splitLocalIsoMinute(start).date || todayDateStr(), t))}
                slots={buildTimeChipSlots({ slotIntervalMinutes })}
                slotIntervalMinutes={slotIntervalMinutes}
                testId="new-appt-time"
                className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
                placeholder="Pick time"
              />
            </div>
            {fromSlot ? (
              <button
                onClick={() => setEditingContext(false)}
                className="text-[11px] font-medium text-slate-500 hover:text-slate-800"
              >
                Done editing
              </button>
            ) : null}
          </div>
        )}

        {/* ── Patient ────────────────────────────────────────────────── */}
        <div>
          <Label required>Patient</Label>
          {picked ? (
            <div className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-md px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{picked.full_name}</p>
                <p className="text-[11px] text-slate-500 truncate">{picked.email || picked.phone || ""}</p>
              </div>
              <button
                onClick={() => { setPicked(null); setPatientSearch(""); setNewPatientOpen(false); }}
                className="text-xs font-semibold text-slate-500 hover:text-slate-800"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative" ref={patientDropRef}>
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              <input
                type="text" value={patientSearch}
                onChange={(e) => {
                  setPatientSearch(e.target.value);
                  setPatientDropOpen(true);
                  if (tutorialDemo) window.dispatchEvent(new CustomEvent("tutorial:booking-patient-search-typed"));
                }}
                onFocus={() => setPatientDropOpen(true)}
                placeholder="Search by name, email, or phone…"
                autoFocus
                data-testid="new-appt-patient-search"
                data-tutorial-id="booking-patient-search"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
              />
              {searching ? (
                <p className="text-[11px] text-slate-400 mt-1"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Searching…</p>
              ) : null}
              {patientDropOpen && patients.length > 0 ? (
                <ul className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                  {patients.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => { setPicked(p); setPatientDropOpen(false); }}
                        data-testid={`new-appt-patient-pick-${p.id}`}
                        data-tutorial-id={tutorialDemo ? "booking-demo-patient" : undefined}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                        <div className="min-w-0">
                          <p className="text-sm text-slate-800 truncate">{p.full_name}</p>
                          <p className="text-[11px] text-slate-500 truncate">{p.email || p.phone || "—"}</p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  const seededName = patientSearch.trim();
                  setNewPatient((prev) => ({
                    ...prev,
                    full_name: prev.full_name || seededName,
                  }));
                  setNewPatientOpen((v) => !v);
                  setPatientDropOpen(false);
                }}
                data-testid="new-appt-new-patient-toggle"
                data-tutorial-id="booking-new-patient-toggle"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 hover:text-teal-900"
              >
                <UserPlus className="w-3.5 h-3.5" />
                New patient
              </button>
              {newPatientOpen ? (
                <form
                  onSubmit={handleCreatePatient}
                  data-testid="new-appt-new-patient-form"
                  className="mt-2 rounded-lg border border-teal-200 bg-teal-50/70 p-3 space-y-3"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="sm:col-span-2">
                      <Label required>Full name</Label>
                      <input
                        type="text"
                        value={newPatient.full_name}
                        onChange={(e) => setNewPatient({ ...newPatient, full_name: e.target.value })}
                        data-testid="new-appt-new-patient-name"
                        className="w-full px-2.5 py-1.5 text-sm border border-teal-200 bg-white rounded focus:outline-none focus:border-teal-500"
                        placeholder="Juan Dela Cruz"
                      />
                    </div>
                    <div>
                      <Label required>Phone</Label>
                      <input
                        type="tel"
                        value={newPatient.phone}
                        onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })}
                        data-testid="new-appt-new-patient-phone"
                        className="w-full px-2.5 py-1.5 text-sm border border-teal-200 bg-white rounded focus:outline-none focus:border-teal-500"
                        placeholder="09171234567"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <input
                        type="email"
                        value={newPatient.email}
                        onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                        data-testid="new-appt-new-patient-email"
                        className="w-full px-2.5 py-1.5 text-sm border border-teal-200 bg-white rounded focus:outline-none focus:border-teal-500"
                        placeholder="patient@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Preferred contact</Label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {CONTACT_METHODS.map((method) => {
                        const selected = newPatient.preferred_contact_method === method.value;
                        return (
                          <button
                            key={method.value}
                            type="button"
                            onClick={() => setNewPatient({ ...newPatient, preferred_contact_method: method.value })}
                            data-testid={`new-appt-new-patient-contact-${method.value}`}
                            className={[
                              "px-2.5 py-1.5 text-xs font-semibold rounded border",
                              selected
                                ? "bg-teal-600 text-white border-teal-600"
                                : "bg-white text-slate-700 border-teal-200 hover:border-teal-400",
                            ].join(" ")}
                          >
                            {method.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => { setNewPatientOpen(false); setNewPatient(initialNewPatient()); }}
                      disabled={creatingPatient}
                      className="px-3 py-1.5 text-xs font-semibold rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creatingPatient}
                      data-testid="new-appt-create-patient"
                      className="px-3 py-1.5 text-xs font-semibold rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                    >
                      {creatingPatient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                      Create & use
                    </button>
                  </div>
                </form>
              ) : null}
            </div>
          )}
        </div>

        {picked && !selectedOrthoRow && (loadingBookablePlanWork || visibleBookingRows.length > 0) ? (
          <div data-testid="new-appt-active-mv-overview">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <Label>{bookableBookingCount > 0 ? "Treatment to book" : "Active treatment"}</Label>
              {loadingBookablePlanWork ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Checking bookings...
                </span>
              ) : (
                <span className="text-[11px] font-semibold text-violet-700">
                  {bookableBookingCount > 0
                    ? `${bookableBookingCount} booking${bookableBookingCount === 1 ? "" : "s"} to make`
                    : "Active MV"}
                </span>
              )}
            </div>
            {!loadingBookablePlanWork ? (
              <div>
                <p className="mb-2 text-[11px] leading-4 text-violet-800">
                  Each MV code makes one appointment. Every active treatment in that MV is included.
                </p>
                <div className="divide-y divide-violet-100 overflow-hidden rounded-lg border border-violet-200 bg-white">
                  {visibleBookingRows.map((row) => row.row_kind === "mv_group" ? (
                    <div
                      key={row.row_id}
                      className={[
                        "flex items-start justify-between gap-3 px-3 py-2.5",
                        selectedMvPlanGroup?.plan_id === row.plan_id ? "bg-violet-50" : "bg-white",
                      ].join(" ")}
                      data-testid={`new-appt-mv-group-${row.plan_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-600">
                          MV · {row.plan_code || "Active MV"}
                        </p>
                        <p className="text-xs font-bold text-slate-900">
                          {row.items.length} treatment{row.items.length === 1 ? "" : "s"} booked together
                        </p>
                        <ul className="mt-1 space-y-1" aria-label={`${row.plan_code || "MV"} treatments`}>
                          {row.items.map((item) => (
                            <li key={item.item_id} className="text-[11px] leading-4 text-slate-600">
                              <span className="font-semibold text-slate-800">{item.procedure_name || "Ongoing treatment"}</span>
                              {` · ${mvItemScopeLabel(item)}`}
                              {Number(item.visits_done || 0) > 0 ? ` · ${item.visits_done} visit${Number(item.visits_done) === 1 ? "" : "s"} recorded` : ""}
                              {item.next_visit_label ? ` · ${item.next_visit_label}` : ""}
                            </li>
                          ))}
                        </ul>
                        {Number(row.balance_centavos || 0) > 0 ? (
                          <p className="mt-1 text-[11px] font-semibold text-slate-700">
                            {formatPHP(row.balance_centavos)} remaining for this MV
                          </p>
                        ) : null}
                      </div>
                      {row.booking_available ? (
                        <button
                          type="button"
                          onClick={() => selectMvPlanForBooking(row)}
                          aria-pressed={selectedMvPlanGroup?.plan_id === row.plan_id}
                          className={[
                            "shrink-0 rounded-md border px-2.5 py-1.5 text-[11px] font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1",
                            selectedMvPlanGroup?.plan_id === row.plan_id
                              ? "border-violet-600 bg-violet-600 text-white"
                              : "border-violet-300 bg-white text-violet-700 hover:border-violet-500 hover:bg-violet-50",
                          ].join(" ")}
                          data-testid={`new-appt-mv-group-book-${row.plan_id}`}
                        >
                          {selectedMvPlanGroup?.plan_id === row.plan_id
                            ? "Selected"
                            : `Book ${row.plan_code || "MV"} next visit`}
                        </button>
                      ) : (
                        <span
                          className="shrink-0 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-[11px] font-bold text-sky-700"
                          data-testid={`new-appt-mv-group-unavailable-${row.plan_id}`}
                        >
                          Appointment booked
                        </span>
                      )}
                    </div>
                  ) : (
                    <div
                      key={row.row_id}
                      className="flex items-start justify-between gap-3 px-3 py-2.5"
                      data-testid={`new-appt-active-mv-overview-item-${row.item_id}`}
                    >
                      <div className="min-w-0">
                        <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-600">
                          Treatment Plan · {row.plan_code || "Active plan"}
                        </p>
                        <p className="truncate text-xs font-bold text-slate-900">
                          {row.procedure_name || "Ongoing treatment"}
                        </p>
                        <p className="mt-0.5 text-[11px] leading-4 text-slate-500">
                          {mvItemScopeLabel(row)}
                          {Number(row.visits_done || 0) > 0 ? ` · ${row.visits_done} visit${Number(row.visits_done) === 1 ? "" : "s"} recorded` : ""}
                          {row.next_visit_label ? ` · ${row.next_visit_label}` : ""}
                          {Number(row.balance_centavos || 0) > 0 ? ` · ${formatPHP(row.balance_centavos)} remaining` : ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => selectPlanItemForBooking(row)}
                        className="shrink-0 rounded-md border border-violet-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-violet-700 transition hover:border-violet-500 hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1"
                        data-testid={`new-appt-active-mv-book-${row.item_id}`}
                      >
                        Book next visit
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ── Appointment type ──────────────────────────────────────── */}
        {picked ? (
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label>Appointment Type</Label>
              {loadingOrthoRows ? (
                <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Checking cases...
                </span>
              ) : null}
            </div>
            {orthoRows.length > 0 ? (
              <div className="rounded-lg border border-teal-100 bg-teal-50/60 p-3 space-y-3">
                <div
                  role="radiogroup"
                  aria-label="Appointment Type"
                  className="grid grid-cols-2 gap-1.5"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!pickedOrthoCaseId}
                    onClick={selectRegularVisit}
                    data-testid="new-appt-appointment-type-regular"
                    className={[
                      "min-h-9 rounded-md border px-3 py-2 text-sm font-semibold transition",
                      !pickedOrthoCaseId
                        ? "border-teal-600 bg-teal-600 text-white"
                        : "border-teal-200 bg-white text-slate-700 hover:border-teal-400",
                    ].join(" ")}
                  >
                    Regular Visit
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!!pickedOrthoCaseId}
                    onClick={selectOrthoVisit}
                    data-testid="new-appt-appointment-type-ortho"
                    className={[
                      "min-h-9 rounded-md border px-3 py-2 text-sm font-semibold transition",
                      pickedOrthoCaseId
                        ? "border-teal-600 bg-teal-600 text-white"
                        : "border-teal-200 bg-white text-slate-700 hover:border-teal-400",
                    ].join(" ")}
                  >
                    Ortho Visit
                  </button>
                </div>
                {pickedOrthoCaseId && orthoRows.length > 1 ? (
                  <div>
                    <Label>Ortho case</Label>
                    <select
                      value={pickedOrthoCaseId}
                      onChange={(e) => setPickedOrthoCaseId(e.target.value)}
                      data-testid="new-appt-ortho-case"
                      className="w-full px-2.5 py-1.5 text-sm border border-teal-200 bg-white rounded focus:outline-none focus:border-teal-500"
                    >
                      {orthoRows.map((row) => (
                        <option key={row.case.id} value={row.case.id}>
                          {orthoCaseLabel(row)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {selectedOrthoRow ? (
                  <>
                    <div className="grid grid-cols-2 gap-1.5">
                      {orthoVisitChoices(selectedOrthoRow).map((choice) => {
                        const selected = orthoVisitType === choice.value;
                        return (
                          <button
                            key={choice.value}
                            type="button"
                            onClick={() => setOrthoVisitType(choice.value)}
                            data-testid={`new-appt-ortho-visit-${choice.value}`}
                            className={[
                              "px-2.5 py-1.5 text-xs font-semibold rounded border text-left",
                              selected
                                ? "bg-teal-600 text-white border-teal-600"
                                : "bg-white text-slate-700 border-teal-200 hover:border-teal-400",
                            ].join(" ")}
                          >
                            {choice.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-teal-800">
                      Doctor View will show the inline Ortho {labelize(orthoVisitType || "visit")} panel for this appointment.
                      {selectedOrthoRow.billing?.per_visit_charge_centavos
                        ? ` Charge due: ${formatPHP(selectedOrthoRow.billing.per_visit_charge_centavos)}.`
                        : ""}
                    </p>
                  </>
                ) : null}
              </div>
            ) : (
              <p className="text-[11px] text-slate-400">
                No active ortho case found for this patient.
              </p>
            )}
          </div>
        ) : null}

        {/* ── Procedure (optional) — booking is a to-do; the procedure is just
              the reason for the visit, not the bill. Front desk can book with
              only a note. The booking logic already accepts a null procedure. */}
        <div>
          <Label>
            {selectedMvPlanGroup
              ? "Treatment"
              : <>Procedure <span className="font-normal text-slate-400">(optional)</span></>}
          </Label>
          {selectedMvPlanGroup ? (
            <div
              className="flex items-start justify-between gap-3 rounded-md border border-violet-200 bg-violet-50 px-3 py-2.5"
              data-testid="new-appt-mv-group-linked"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-violet-950">
                  {selectedMvPlanGroup.plan_code || "MV"} next visit
                </p>
                <p className="mt-0.5 text-[11px] leading-4 text-violet-800">
                  One appointment will include all {selectedMvPlanGroup.items.length} active treatment{selectedMvPlanGroup.items.length === 1 ? "" : "s"} in this MV.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPickedMvPlanId("")}
                className="shrink-0 text-xs font-semibold text-violet-700 hover:text-violet-950"
              >
                Change
              </button>
            </div>
          ) : selectedOrthoRow ? (
            <div className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2">
              <p className="text-sm font-semibold text-slate-900">
                Case-bound {labelize(orthoVisitType || "ortho")} procedure
              </p>
              <p className="text-[11px] text-teal-800 mt-0.5">
                Dental OS will use this case&apos;s locked procedure binding at booking. Front desk only picks the slot and duration.
              </p>
            </div>
          ) : pickedProc ? (
            <div className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-md px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{pickedProc.name}</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {pickedProc.code || "—"}
                  {pickedProc.suggested_duration_minutes ? ` · ${pickedProc.suggested_duration_minutes} min` : ""}
                </p>
              </div>
              <button
                onClick={() => { setPickedProc(null); setProcSearch(""); }}
                className="text-xs font-semibold text-slate-500 hover:text-slate-800"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative" ref={procDropRef}>
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              <input
                type="text" value={procSearch}
                onChange={(e) => {
                  setProcSearch(e.target.value);
                  setProcDropOpen(true);
                  if (tutorialDemo) window.dispatchEvent(new CustomEvent("tutorial:booking-procedure-search-typed"));
                }}
                onFocus={() => setProcDropOpen(true)}
                placeholder="Search procedure…"
                data-testid="new-appt-proc-search"
                data-tutorial-id="booking-procedure-search"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
              />
              {procDropOpen && filteredProcs.length > 0 ? (
                <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto border border-slate-200 rounded-md bg-white shadow-lg divide-y divide-slate-100">
                  {filteredProcs.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => { setPickedProc(p); setProcDropOpen(false); }}
                        data-testid={`new-appt-proc-pick-${p.id}`}
                        data-tutorial-id={tutorialDemo ? "booking-demo-procedure" : undefined}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50"
                      >
                        <p className="text-sm text-slate-800 truncate">{p.name}</p>
                        <p className="text-[11px] text-slate-500 truncate">
                          {p.code || p.short_code || "—"}
                          {p.suggested_duration_minutes ? ` · ${p.suggested_duration_minutes} min` : ""}
                          {` · ${formatPHP(p.default_fee_centavos)}`}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {/* No catalog match — don't block front desk. Let what they typed
                  survive as the visit's reason (folded into the notes below),
                  matching the paper-clinic flow. */}
              {procDropOpen && procSearch.trim() && filteredProcs.length === 0 ? (
                <ul className="absolute z-10 mt-1 w-full border border-slate-200 rounded-md bg-white shadow-lg">
                  <li>
                    <button
                      onClick={() => {
                        const text = procSearch.trim();
                        setNotes((n) => (n && n.trim()) ? `${text}\n${n}` : text);
                        setProcSearch("");
                        setProcDropOpen(false);
                      }}
                      data-testid="new-appt-proc-use-as-note"
                      className="w-full text-left px-3 py-2 hover:bg-slate-50"
                    >
                      <p className="text-sm text-slate-800">
                        Use &ldquo;<span className="font-semibold">{procSearch.trim()}</span>&rdquo; as the reason
                      </p>
                      <p className="text-[11px] text-slate-500">Not in your list — we&apos;ll save it in the notes below.</p>
                    </button>
                  </li>
                </ul>
              ) : null}
            </div>
          )}
          {picked && pickedProc && !selectedOrthoRow && (matchingMvPlanGroup || matchingPlanItems.length > 0 || (ongoingTreatmentEnabled && isGenericMvProcedure(pickedProc))) ? (
            <div className="mt-2">
              {loadingBookablePlanWork ? (
                <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  Checking existing treatment…
                </p>
              ) : matchingMvPlanGroup ? (
                <div
                  className="flex items-start justify-between gap-3 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5"
                  data-testid={`new-appt-mv-procedure-belongs-${matchingMvPlanGroup.plan_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-violet-950">
                      Part of {matchingMvPlanGroup.plan_code || "an active MV"}
                    </p>
                    <p className="mt-0.5 text-[11px] leading-4 text-violet-800">
                      Book the MV as one appointment so its {matchingMvPlanGroup.items.length} active treatment{matchingMvPlanGroup.items.length === 1 ? "" : "s"} stay together.
                    </p>
                  </div>
                  {matchingMvPlanGroup.booking_available ? (
                    <button
                      type="button"
                      onClick={() => selectMvPlanForBooking(matchingMvPlanGroup)}
                      className="shrink-0 rounded-md border border-violet-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-violet-700 hover:border-violet-500 hover:bg-violet-100"
                    >
                      Book {matchingMvPlanGroup.plan_code || "MV"}
                    </button>
                  ) : (
                    <span className="shrink-0 text-[11px] font-semibold text-sky-700">Appointment booked</span>
                  )}
                </div>
              ) : matchingPlanItems.length > 0 ? (
                <div
                  className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2.5"
                  data-testid="new-appt-active-mv-linked"
                >
                  <div className="flex items-start gap-2.5">
                    <History className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-teal-950">
                        {selectedPlanItem?.booking_available === false ? "Active" : "Continue"} {selectedPlanItem?.plan_code || matchingPlanItems[0]?.plan_code || "existing treatment"}
                      </p>
                      <p className="mt-0.5 text-[11px] text-teal-800">
                        {selectedPlanItem?.booking_available === false
                          ? `This MV is already active and no additional booking is currently available. Open the existing appointment; do not import or duplicate this ${pickedProc.name} case.`
                          : `This appointment will use the existing case. It will not create another ${pickedProc.name} case.`}
                      </p>
                      {matchingPlanItems.length > 1 ? (
                        <select
                          value={pickedMvItemId}
                          onChange={(event) => setPickedMvItemId(event.target.value)}
                          data-testid="new-appt-active-mv-item"
                          className="mt-2 w-full rounded border border-teal-200 bg-white px-2.5 py-1.5 text-xs text-slate-800"
                        >
                          <option value="">Choose case scope…</option>
                          {matchingPlanItems.map((item) => (
                            <option key={item.item_id} value={item.item_id}>
                              {mvItemScopeLabel(item)} · {item.procedure_name || pickedProc.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="mt-1 text-[11px] font-semibold text-teal-900">
                          {mvItemScopeLabel(matchingPlanItems[0])}
                          {matchingPlanItems[0]?.next_visit_label ? ` · ${matchingPlanItems[0].next_visit_label}` : ""}
                          {Number(matchingPlanItems[0]?.balance_centavos || 0) > 0
                            ? ` · ${formatPHP(matchingPlanItems[0].balance_centavos)} remaining`
                            : ""}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : canImportOngoingCase && isGenericMvProcedure(pickedProc) && mvImportedItem ? (
                <p className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700" data-testid="new-appt-mv-import-linked">Imported ongoing case linked</p>
              ) : canImportOngoingCase && isGenericMvProcedure(pickedProc) ? (
                <button
                  type="button"
                  onClick={() => setMvImportOpen(true)}
                  className="group flex w-full items-center justify-between gap-3 rounded-lg border border-violet-300 bg-violet-50 px-3 py-2.5 text-left shadow-sm transition hover:border-violet-500 hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                  data-testid="new-appt-import-ongoing-case"
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-violet-600 text-white">
                      <History className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs font-bold text-violet-950">Treatment already in progress?</span>
                      <span className="block text-[11px] font-semibold text-violet-700">Import the existing MV case</span>
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-violet-600 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* ── Duration pills ─────────────────────────────────────────── */}
        <div data-tutorial-id="booking-duration">
          <Label>Duration</Label>
          <div className="flex flex-wrap gap-1.5">
            {durationOptions.map((m) => (
              <button
                key={m}
                onClick={() => setDurationMin(m)}
                data-testid={`new-appt-dur-${m}`}
                data-tutorial-id={tutorialDemo && m === 45 ? "booking-duration-45" : undefined}
                className={[
                  "px-3 py-1 text-xs rounded-md border",
                  durationMin === m
                    ? "bg-teal-600 text-white border-teal-600 font-semibold"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
                ].join(" ")}
              >
                {m}m
              </button>
            ))}
          </div>
          {pediaChairWarning ? (
            <div
              className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="new-appt-pedia-chair-warning"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{pediaChairWarning.title}</p>
                  <p className="mt-0.5 leading-relaxed">{pediaChairWarning.message}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setDurationMin((m) => Number(m || 0) + pediaChairWarning.suggestedExtraMinutes)}
                  className="shrink-0 rounded border border-amber-300 bg-white px-2 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100"
                  data-testid="new-appt-pedia-add-time"
                >
                  +{pediaChairWarning.suggestedExtraMinutes}m
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* ── Doctor (always shown) ──────────────────────────────────── */}
        <div>
          <Label>Doctor</Label>
          <select
            value={doctorId} onChange={(e) => setDoctorId(e.target.value)}
            data-testid="new-appt-doctor"
            data-tutorial-id="booking-doctor"
            className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded"
          >
            <option value="">Unassigned</option>
            {doctors.map((d) => <option key={d.id} value={d.id}>{formatDoctorName(d, "Unnamed")}</option>)}
          </select>
          {/* Substitute booking notice — fires when the user picks any
              dentist other than the owning one. Booking ≠ reassignment;
              the owning dentist on file is unchanged regardless of who
              actually performs this visit. */}
          {owningDentistId && doctorId && doctorId !== owningDentistId ? (
            <p className="mt-1 text-[11px] text-amber-700" data-testid="new-appt-substitute-notice">
              Substitute booking — {doctors.find((d) => d.id === owningDentistId)?.display_name ?? "the owning dentist"} stays on file as this patient's owning dentist.
            </p>
          ) : null}
        </div>

        {/* Chief complaint / notes — surfaced on the main form (not hidden)
            so front desk fills it in; the doctor reads it in Chairside. */}
        <div>
          <Label>Chief complaint / notes</Label>
          <textarea
            rows={2} value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Toothache on lower right · patient nervous"
            data-testid="new-appt-notes"
            className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
          />
        </div>

        {/* ── More options (collapsible) ─────────────────────────────── */}
        <div className="border-t border-slate-100 pt-2">
          <button
            onClick={() => setMoreOpen((v) => !v)}
            className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900"
            data-testid="new-appt-more-toggle"
            data-tutorial-id="booking-more-options"
          >
            {moreOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            More options
          </button>
          {moreOpen ? (
            <div className="mt-3 space-y-3">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox" checked={walkIn}
                  onChange={(e) => setWalkIn(e.target.checked)}
                  data-testid="new-appt-walkin"
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-300"
                />
                Walk-in (check in immediately)
              </label>
            </div>
          ) : null}
        </div>

        {error ? (
          <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </div>
        ) : null}
      </div>

      {/* ── Sticky footer ──────────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-white">
        <button
          onClick={onCancel} disabled={busy}
          className="px-4 py-2 text-sm border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleBook()} disabled={busy || !canBook}
          data-testid="new-appt-book"
          data-tutorial-id="booking-submit"
          className="px-5 py-2 text-sm font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {walkIn ? "Book & Check In" : "Book"}
        </button>
      </div>
      {canImportOngoingCase ? (
        <MvCaseImportDialog
          open={mvImportOpen}
          patientId={picked?.id}
          patientName={picked?.full_name}
          sourceEntryPoint="calendar"
          procedures={procedures}
          doctors={doctors}
          initialProcedure={pickedProc}
          initialDoctorId={doctorId}
          onClose={() => setMvImportOpen(false)}
          onImported={(result) => {
            setMvImportedItem(result);
            if (result?.doctor_id) setDoctorId(result.doctor_id);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------
function Label({ children, required }) {
  return (
    <label className="block text-xs font-semibold text-slate-700 mb-1">
      {children}{required ? <span className="text-rose-500"> *</span> : null}
    </label>
  );
}

function toLocalIsoMinute(isoOrDate) {
  return clinicIsoMinuteFromInstant(isoOrDate);
}

// Split / join the "YYYY-MM-DDTHH:MM" local iso string the panel uses for
// the Start field. The new date input + time chip grid manipulate these
// two halves independently and recombine on every change.
function splitLocalIsoMinute(iso) {
  if (!iso || typeof iso !== "string") return { date: "", time: "" };
  const [date = "", time = ""] = iso.split("T");
  return { date, time: time.slice(0, 5) };
}

function joinLocalIsoMinute(date, time) {
  if (!date) return "";
  return `${date}T${time || "09:00"}`;
}

function todayDateStr() {
  return clinicTodayDateString();
}

function minutesBetween(a, b, fallback = 5) {
  const da = a instanceof Date ? a : new Date(a);
  const db = b instanceof Date ? b : new Date(b);
  const fallbackMin = normalizeSlotIntervalMinutes(fallback);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return fallbackMin;
  return Math.max(fallbackMin, Math.round((db.getTime() - da.getTime()) / 60_000));
}

function formatDateLine(isoLocal) {
  if (!isoLocal) return "";
  const d = clinicInstantFromIsoMinute(isoLocal);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: CLINIC_TIME_ZONE });
}

function formatTime(isoLocal) {
  if (!isoLocal) return "";
  const d = clinicInstantFromIsoMinute(isoLocal);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: CLINIC_TIME_ZONE });
}

function formatPHP(centavos) {
  if (centavos == null) return "Depends on case";
  const pesos = (centavos || 0) / 100;
  return `₱${pesos.toLocaleString("en-PH", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function defaultOrthoVisitType(row) {
  const status = row?.case?.status;
  if (status === BONDING_READY_STATUS) return "bonding";
  if (status === "retention") return "retention";
  return "adjustment";
}

function orthoVisitChoices(row) {
  const status = row?.case?.status;
  if (status === BONDING_READY_STATUS) {
    return [
      { value: "bonding", label: "Bonding" },
    ];
  }
  if (status === "retention") {
    return [
      { value: "retention", label: "Retention check" },
    ];
  }
  return [
    { value: "adjustment", label: "Adjustment" },
    { value: "debonding", label: "Debonding" },
  ];
}

function orthoCaseLabel(row) {
  const system = row?.case?.system_name || row?.case?.system_display_name || row?.case?.system || "Ortho case";
  const visitsDone = row?.visits?.length || 0;
  const total = row?.case?.estimated_total_visits || row?.billing?.estimated_total_visits || "?";
  return `${system} · Visit ${visitsDone + 1} of ${total}`;
}

function labelize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function initialNewPatient() {
  return {
    full_name: "",
    phone: "",
    email: "",
    preferred_contact_method: "no_preference",
  };
}
